/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
/**
 * ProjectPersistence.ts
 * ---------------------
 * Implements persistence logic for the Project State.
 *
 * - Provides functions to serialize (stringify), deserialize (parse), and isolate (extract) the storable subset of ProjectState.
 * - Handles merging loaded project data into current runtime state.
 *
 * This file is responsible for ensuring only serializable, cycle-free data is saved or loaded, and is used by saving/loading routines and API handlers.
 */
import { ProjectState } from "./ProjectModels";
import { ProjectStorableState } from "./ProjectModels";
import { IDLE } from "../Informedb/PromiseState";

// When saving a state, only include the persistent values, not the transient/"working" values
export function stringify(state: ProjectState): string {
  const obj = isolateProject(state);
  return JSON.stringify(obj, null, 2);
}

export function parse(state: ProjectState, json: string): ProjectState {
  const parsedObject = JSON.parse(json);
  const newProject = new ProjectStorableState();

  // copy storable fields into new project
  Object.keys(newProject).forEach((key) => {
    if (key in parsedObject) {
      newProject[key] = parsedObject[key];
    } else {
      throw Error("Could not find '" + key + "'");
    }
  });

  return mergeProject(state, newProject);
}

export function isolateProject(state: ProjectState): ProjectStorableState {
  const obj = new ProjectStorableState();

  Object.keys(obj).forEach((key) => {
    // If copying nodes, strip mappingResult
    if (key === "rfNodes") {
      obj[key] = state.rfNodes.map((node) => ({
        ...node,
        data: {
          ...node.data,
          mappingResult: undefined, // Or simply do not include this property
        },
      }));
    } else {
      obj[key] = (key in state ? state : obj)[key];
    }
  });

  return obj;
}

export function mergeProject(
  state: ProjectState,
  newProject: ProjectStorableState
): ProjectState {
  // build the next state from the current state, the storable fields, and some cleared transient fields
  return {
    ...state,
    ...newProject,
    dataLoadAsync: IDLE,
    performMappingAsync: IDLE,
    saveMappingAsync: IDLE,
    edgeClicked: false,
    rerenderKey: state.rerenderKey + 1,
  };
}
